import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "@src/utils/baseQuery";
import { StudentOrgData } from "./RegistrationTemplateApiSlice";
import { APIResponse } from "./CommonApiSlice";
import { PaginationType } from "@src/types/CommonTypes";
import { Trainer } from "@src/pages/super-admin/dashboard/trainerListDrawer/TrainerListDrawer.types";
import {
  UsersResponse,
  User,
  RegistrationData,
} from "@src/pages/users/users.types";

interface AddUserPayload {
  name: string;
  email: string;
  phoneNumber: string;
  privileges: string[];
  organizationId: string;
}
export interface StudentCountData {
  organizationId: string;
  newCount: number;
  approvedCount: number;
  inActiveCount: number;
  allCount: number;
}

export interface StudentCountResponse {
  statusCode: number;
  httpStatus: string;
  response: StudentCountData;
}

export interface Student {
  _id?: string;
  studentId: string;
  name: string | null;
  organizationName: string | null;
  studentOrgActivationStatus: string | null;
  additionalDetails?: StudentOrgData;
  allSelected?: boolean;
  status?: string;
  checked?: boolean;
  paidStatus?: boolean | null;
}

export interface Pageable {
  pageNumber: number;
  pageSize: number;
  offset: number;
  paged: boolean;
  unpaged: boolean;
}

export interface GetUsersForOrganizationResponse {
  statusCode: number;
  httpStatus: string;
  response: {
    content: Student[];
  } & PaginationType;
}

export interface QuizMetaData {
  quizName: string;
  quizId: string;
  completedOn: string | null;
  duration: number;
  totalQuestions: number;
  totalCorrectAnswered: number;
  totalMarks: number;
  totalScored: number;
}

export interface QuizProfile {
  quizzesTaken: number;
  avgScore: number;
  totalQuestions: number;
  correctQuestions: number;
  quizMetaDataSet: QuizMetaData[];
}

export interface TournamentMetaData {
  tournamentId: string;
  tournamentName: string;
  rank: number | null;
  completedOn: string | null;
  reward: number;
  totalMarks: number;
  totalScore: number;
}

export interface TournamentProfile {
  tournamentTaken: number;
  firstPlaceCount: number;
  topThreeCount: number;
  bestRank: number | null;
  avgScore: number;
  tournamentMetaDataSet: TournamentMetaData[];
}

export interface ProfileResponse {
  statusCode: number;
  httpStatus: string;
  response: {
    quizProfile: QuizProfile;
    tournamentProfile: TournamentProfile;
  };
}

export interface GetTrainerDataApiResponse extends APIResponse {
  response: { content: Trainer[] } & PaginationType;
}

export interface GetAllUsersResponse extends APIResponse {
  response: UsersResponse;
}

export interface GetAllUsersForSubjectResponse extends APIResponse {
  response: UsersResponse;
}

export interface AddUserApiResponse extends APIResponse {
  response: User;
}

export interface AssignUserToSubjectResponse extends APIResponse {
  response: User[];
}
export interface GetAllUnassignedUsersResponse extends APIResponse {
  response: User[];
}

export interface UpdateStudentsCurrentOrgResponse extends APIResponse {
  response: {
    currentOrganization: string;
    id: string;
    logo: string | null;
  };
}

export interface DashboardClientData {
  assessmentsTaken: number;
  elpQuestionsTaken: number;
  practiceQuestionsTaken: number;
  totalAssessments: number;
  totalElpQuestions: number;
  totalPracticeQuestions: number;
}

type UpdateStudentsOrgListResponse = APIResponse;

export interface UpdateStudentRegistrationStatusResponse extends APIResponse {
  response: RegistrationData[];
}

export interface GetDashboardClientResponse extends APIResponse {
  response: DashboardClientData;
}

export const UserApiSlice = createApi({
  reducerPath: "userApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["getAllSubjectUsers"],
  endpoints: (builder) => ({
    getAllUsers: builder.query<
      GetAllUsersResponse,
      { pageNo: number; pageSize: number; organizationId: string }
    >({
      query: ({ pageNo, pageSize, organizationId }) => ({
        url: `user/get-users-orgId?organisationId=${organizationId}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),
    getAllStudentsForOrganization: builder.query<
      any,
      { pageNo: number; pageSize: number; organizationId: string }
    >({
      query: ({ pageNo, pageSize, organizationId }) => ({
        url: `student/get-students-organization?organizationId=${organizationId}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),
    getAllUsersForSubject: builder.query<
      GetAllUsersForSubjectResponse,
      {
        pageNo: number;
        pageSize: number;
        subjectId: string;
        organizationId: string;
      }
    >({
      query: ({ pageNo, pageSize, subjectId, organizationId }) => ({
        url: `user/get-users?subjectId=${subjectId}&pageNo=${pageNo}&pageSize=${pageSize}&organizationId=${organizationId}`,
        method: "GET",
      }),
      providesTags: ["getAllSubjectUsers"],
    }),
    addUsers: builder.mutation<
      AddUserApiResponse,
      { payload: AddUserPayload; adderEmail: string }
    >({
      query: ({ adderEmail, payload }) => ({
        url: `user?adderEmail=${adderEmail}`,
        method: "POST",
        body: payload,
      }),
    }),
    updateUsers: builder.mutation<
      AddUserApiResponse,
      { payload: AddUserPayload }
    >({
      query: ({ payload }) => ({
        url: `user/update-user`,
        method: "PUT",
        body: payload,
      }),
    }),
    assignUserToSubject: builder.mutation<
      AssignUserToSubjectResponse,
      { payload: string[]; subjectId: string }
    >({
      query: ({ subjectId, payload }) => ({
        url: `user/assign?subjectId=${subjectId}`,
        method: "POST",
        body: payload,
      }),
    }),
    getUnassignedStudentsForSubject: builder.query<
      GetUsersForOrganizationResponse,
      {
        organizationId: string;
        subjectId: string;
        pageNo: number;
        pageSize: number;
      }
    >({
      query: ({ organizationId, subjectId, pageNo, pageSize }) => ({
        url: `student/get-unassigned-students-subject?organizationId=${organizationId}&subjectId=${subjectId}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),

    getAssignedStudentsForSubject: builder.query<
      GetUsersForOrganizationResponse,
      {
        organizationId: string;
        subjectId: string;
        pageNo: number;
        pageSize: number;
      }
    >({
      query: ({ organizationId, subjectId, pageNo, pageSize }) => ({
        url: `student/get-assigned-students-subject?organizationId=${organizationId}&subjectId=${subjectId}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),
    getAllUnassignedUsers: builder.query<
      GetAllUnassignedUsersResponse,
      { subjectId: string; organizationId: string }
    >({
      query: ({ subjectId, organizationId }) => ({
        url: `user/get-unassigned-users?subjectId=${subjectId}&organizationId=${organizationId}`,
        method: "GET",
      }),
    }),
    updateStudentCurrentOrg: builder.mutation<
      UpdateStudentsCurrentOrgResponse,
      { organizationId: string }
    >({
      query: ({ organizationId }) => ({
        url: `user/update-currentOrg-student?organizationId=${organizationId}`,
        method: "POST",
      }),
    }),
    updateStudentOrgList: builder.mutation<
      UpdateStudentsOrgListResponse,
      { organizationId: string }
    >({
      query: ({ organizationId }) => ({
        url: `user/update-organization-list?organizationId=${organizationId}`,
        method: "POST",
      }),
    }),
    updateRegistrationStatus: builder.mutation<
      UpdateStudentRegistrationStatusResponse,
      {
        organizationId: string;
        action: string;
        studentIds: (string | undefined)[];
        message?: string;
      }
    >({
      query: ({ organizationId, action, studentIds, message }) => ({
        url: `user/update-registration-status?organizationId=${organizationId}&action=${action}${
          message ? `&message=${encodeURIComponent(message)}` : ""
        }`,
        method: "PUT",
        body: studentIds,
      }),
    }),
    getUsersForOrganization: builder.query<
      GetUsersForOrganizationResponse,
      {
        organizationId: string;
        status: string;
        pageNo: number;
        pageSize: number;
      }
    >({
      query: ({ organizationId, status, pageNo, pageSize }) => ({
        url: `user/get-users-organization?organizationId=${organizationId}&status=${status}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),
    getStudentCountForOrg: builder.query<
      StudentCountResponse,
      {
        organizationId: string;
      }
    >({
      query: ({ organizationId }) => ({
        url: `user/get-student-count?organizationId=${organizationId}`,
        method: "GET",
      }),
    }),
    getUserProfileSummary: builder.query<ProfileResponse, void>({
      query: () => ({
        url: `student/get-student-profile`,
        method: "GET",
      }),
    }),
    getDashboardClient: builder.query<GetDashboardClientResponse, void>({
      query: () => ({
        url: `student/dashboard-client`,
        method: "GET",
      }),
    }),
    getTrainerData: builder.query<
      GetTrainerDataApiResponse,
      {
        subjectId: string;
        organizationId: string;
        pageNo: number;
        pageSize: number;
      }
    >({
      query: ({ subjectId, organizationId, pageNo, pageSize }) => ({
        url: `user/get-trainer-data?subjectId=${subjectId}&organizationId=${organizationId}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetAllUsersQuery,
  useAddUsersMutation,
  useUpdateUsersMutation,
  useUpdateStudentCurrentOrgMutation,
  useUpdateStudentOrgListMutation,
  useGetAllUsersForSubjectQuery,
  useLazyGetAllUsersQuery,
  useAssignUserToSubjectMutation,
  useGetUsersForOrganizationQuery,
  useLazyGetUsersForOrganizationQuery,
  useUpdateRegistrationStatusMutation,
  useGetAllUnassignedUsersQuery,
  useGetStudentCountForOrgQuery,
  useGetUserProfileSummaryQuery,
  useGetDashboardClientQuery,
  useLazyGetAllUsersForSubjectQuery,
  useLazyGetDashboardClientQuery,
  useLazyGetTrainerDataQuery,
  useGetAllStudentsForOrganizationQuery,
  useLazyGetAllStudentsForOrganizationQuery,
  useGetUnassignedStudentsForSubjectQuery,
  useLazyGetUnassignedStudentsForSubjectQuery,
  useGetAssignedStudentsForSubjectQuery,
  useLazyGetAssignedStudentsForSubjectQuery,
} = UserApiSlice;
